import React, { useState, useEffect } from 'react'
import { Clock, ArrowUpDown, Trash2 } from 'lucide-react'
import { ConversionResult } from '@/types/currency'
import { formatCurrency, formatTimeAgo } from '@/utils/formatters'
import { safeStorage, safeJsonParse } from '@/utils/helpers'

interface ConversionHistoryItem extends ConversionResult {
  id: string
}

const ConversionHistory: React.FC = () => {
  const [history, setHistory] = useState<ConversionHistoryItem[]>([])
  const [isExpanded, setIsExpanded] = useState(false)

  // Load history from storage on mount
  useEffect(() => {
    const savedHistory = safeStorage.getItem('conversion_history')
    if (savedHistory) {
      const parsedHistory = safeJsonParse(savedHistory, [])
      setHistory(parsedHistory)
    }
  }, [])

  // Save history to storage whenever it changes
  useEffect(() => {
    if (history.length > 0) {
      safeStorage.setItem('conversion_history', JSON.stringify(history))
    }
  }, [history])

  const addToHistory = (conversion: ConversionResult) => {
    const historyItem: ConversionHistoryItem = {
      ...conversion,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }

    setHistory(prev => {
      const newHistory = [historyItem, ...prev]
      // Keep only the last 20 conversions
      return newHistory.slice(0, 20)
    })
  }

  const removeFromHistory = (id: string) => {
    setHistory(prev => prev.filter(item => item.id !== id))
  }

  const clearHistory = () => {
    setHistory([])
    safeStorage.removeItem('conversion_history')
  }

  const repeatConversion = (item: ConversionHistoryItem) => {
    // This would trigger a new conversion with the same parameters
    // For now, we'll just add it to the top of the history
    const newItem: ConversionHistoryItem = {
      ...item,
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    }
    setHistory(prev => [newItem, ...prev])
  }

  if (history.length === 0) {
    return null
  }

  const displayedHistory = isExpanded ? history : history.slice(0, 5)

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Recent Conversions
        </h3>
        <div className="flex items-center space-x-2">
          {history.length > 5 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
            >
              {isExpanded ? 'Show Less' : `Show All (${history.length})`}
            </button>
          )}
          <button
            onClick={clearHistory}
            className="text-sm text-gray-500 hover:text-red-600 transition-colors"
            title="Clear history"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {displayedHistory.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
          >
            <div className="flex-1">
              <div className="flex items-center space-x-2 text-gray-900">
                <span className="font-medium">
                  {formatCurrency(item.originalAmount, item.fromCurrency.code)}
                </span>
                <ArrowUpDown className="w-4 h-4 text-gray-400" />
                <span className="font-medium">
                  {item.formattedAmount}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-500 mt-1">
                <span>
                  1 {item.fromCurrency.code} = {item.exchangeRate.toFixed(4)} {item.toCurrency.code}
                </span>
                <span>•</span>
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatTimeAgo(item.timestamp)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => repeatConversion(item)}
                className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                title="Repeat conversion"
              >
                <ArrowUpDown className="w-4 h-4" />
              </button>
              <button
                onClick={() => removeFromHistory(item.id)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Remove from history"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {history.length > 5 && !isExpanded && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={() => setIsExpanded(true)}
            className="w-full text-center text-sm text-primary-600 hover:text-primary-700 transition-colors"
          >
            View {history.length - 5} more conversions
          </button>
        </div>
      )}
    </div>
  )
}

export default ConversionHistory